/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { FormBuilder, FormControl } from '@angular/forms';
import { SelectOption } from '@bcl/design-system';
import { Subject, takeUntil } from 'rxjs';
import { StorageService } from 'src/app/services/storage.service';
import { RestService } from 'src/app/services/rest.service';
import { StateService } from 'src/app/services/state.service';

type LanguageCode =
  | 'en-US'
  | 'de-DE'
  | 'fr-FR'
  | 'nl-NL'
  | 'pl-PL'
  | 'es-ES'
  | 'it-IT';

@Component({
  selector: 'voice-recognition',
  templateUrl: './voice-recognition.component.html',
  styleUrls: ['./voice-recognition.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VoiceRecognitionComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @Input()
  public initialText: string | null = null;
  @Output()
  public recordedText = new EventEmitter<string | null>();

  public isRecording = false;
  public temporaryText: string | null = null;
  public finalText: string | null = null;
  public languages: SelectOption<LanguageCode>[] = [
    { value: 'en-US', label: 'English' },
    { value: 'de-DE', label: 'German' },
    { value: 'fr-FR', label: 'French' },
    { value: 'nl-NL', label: 'Dutch' },
    { value: 'pl-PL', label: 'Polish' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'it-IT', label: 'Italian' },
  ];
  public selectedLanguage!: FormControl;

  @ViewChild('temporaryTextContainer')
  private temporaryTextContainer!: ElementRef;

  private readonly destroyed$ = new Subject<void>();
  private recognition: any;
  private currentTranscript = '';
  private resultTimeout: any = null;

  // Language mapping from app language codes to speech recognition language codes
  private readonly languageMapping: { [key: string]: LanguageCode } = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    nl: 'nl-NL',
    pl: 'pl-PL',
    es: 'es-ES',
    it: 'it-IT',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private fb: FormBuilder,
    private storage: StorageService,
    private rest: RestService,
    private stateService: StateService
  ) {}

  public ngOnInit(): void {
    this.initializeLanguage();
    this.finalText = this.initialText;

    this.selectedLanguage.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (this.recognition) {
          this.recognition.lang = value;
          if (this.isRecording) {
            const wasRecording = this.isRecording;

            this.stopRecording();
            setTimeout(() => {
              if (wasRecording) {
                this.startRecording();
              }
            }, 250);
          }
        }
      });

    // Subscribe to language changes from the app
    this.stateService.stateChanged
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        const appLanguage = state.get('language');
        if (appLanguage) {
          this.updateLanguageFromApp(appLanguage);
        }
      });

    this.initializeSpeechRecognition();
  }

  public ngAfterViewChecked() {
    this.scrollToBottom();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.resultTimeout) {
      clearTimeout(this.resultTimeout);
    }
  }

  public startRecording(): void {
    if (!this.isRecording && this.recognition) {
      try {
        this.isRecording = true;
        this.currentTranscript = '';
        this.recognition?.start();
      } catch (e) {
        console.error('Error starting speech recognition: ', e);
      }
    }
  }

  public stopRecording(): void {
    this.isRecording = false;
    this.recognition?.stop();
    this.temporaryText = null;
    if (this.resultTimeout) {
      clearTimeout(this.resultTimeout);
    }
    // Process any remaining text
    if (this.currentTranscript.trim()) {
      this.appendFinalText(this.currentTranscript.trim());
    }
    this.currentTranscript = '';
  }

  private appendFinalText(finalTranscript: string) {
    if (finalTranscript.trim() !== '') {
      this.finalText ? null : (this.finalText = '');
      this.finalText += (this.finalText ? '\n' : '') + finalTranscript.trim();
      this.temporaryText = null;
      this.recordedText.emit(this.finalText);
      this.cdr.detectChanges();
    }
  }

  private scrollToBottom(): void {
    try {
      this.temporaryTextContainer.nativeElement.scrollTop =
        this.temporaryTextContainer.nativeElement.scrollHeight;
    } catch (err) {}
  }

  private async initializeLanguage(): Promise<void> {
    // Try to get language from state service first
    let appLanguage = this.stateService.getValue('language');

    if (!appLanguage) {
      // Fallback to storage
      appLanguage = await this.storage.get('language');
    }

    if (!appLanguage) {
      // Fallback to rest service
      appLanguage = this.rest.selectedLanguage;
    }

    if (!appLanguage) {
      // Default to English
      appLanguage = 'en';
    }

    // Clean up the language code (remove quotes if present)
    appLanguage = appLanguage.replaceAll('"', '');

    // Map to speech recognition language code
    const speechLanguage = this.languageMapping[appLanguage] || 'en-US';

    this.selectedLanguage = this.fb.control(speechLanguage);
  }

  private updateLanguageFromApp(appLanguage: string): void {
    // Clean up the language code (remove quotes if present)
    const cleanLanguage = appLanguage.replaceAll('"', '');

    // Map to speech recognition language code
    const speechLanguage = this.languageMapping[cleanLanguage] || 'en-US';

    // Update the form control if the language has changed
    if (this.selectedLanguage.value !== speechLanguage) {
      this.selectedLanguage.setValue(speechLanguage);
    }
  }

  private initializeSpeechRecognition(): void {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = true;
      this.recognition.lang = this.selectedLanguage.value;

      this.recognition.onstart = () => {
        this.cdr.detectChanges();
      };

      this.recognition.onend = () => {
        if (this.isRecording) {
          // Restart recognition after a short delay
          setTimeout(() => {
            if (this.isRecording) {
              this.recognition?.start();
            }
          }, 100);
        }
        this.cdr.detectChanges();
      };

      this.recognition.onerror = (event: any) => {
        console.error('Speech recognition error detected: ' + event.error);
        if (event.error === 'no-speech') {
          // Handle no-speech error by restarting if still recording
          if (this.isRecording) {
            this.recognition?.stop();
            setTimeout(() => {
              if (this.isRecording) {
                this.recognition?.start();
              }
            }, 100);
          }
        } else {
          this.isRecording = false;
        }
        this.cdr.detectChanges();
      };

      this.recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');

        // Clear previous timeout
        if (this.resultTimeout) {
          clearTimeout(this.resultTimeout);
        }

        // Update current transcript
        this.currentTranscript = transcript;
        this.temporaryText = transcript;

        // Set timeout to finalize the text
        this.resultTimeout = setTimeout(() => {
          if (this.currentTranscript.trim()) {
            this.appendFinalText(this.currentTranscript.trim());
            this.currentTranscript = '';
          }
        }, 1500); // Adjust this timeout as needed

        this.cdr.detectChanges();
      };
    } else {
      console.error('Speech recognition not supported in this browser.');
    }
  }
}
